/**
 * The portal API (`/api/portal/*` on the page's own origin). Every call carries the Entra access token from the
 * IDC Platform SDK shell as a bearer token and every response is parsed (./schemas.ts). Failures are ApiError: the
 * app turns a 401 into sign-in-again (useLoad / useAction) and a 403 into a friendly message.
 */
import type { z } from "zod";
import type { ReportReason } from "@swtracker/core/analyst-profile";
import type { OnboardingAction } from "@swtracker/core/onboarding";
import type { Auth } from "../auth/session.ts";
import * as s from "./schemas.ts";
import { ReadPool } from "./readPool.ts";
import { PRACTICE_HEADER, practiceHeaderApplies, practiceMutationAllowed } from "../practice/practiceRoutes.ts";
import { activePracticeId, endPractice } from "../practice/practiceStore.ts";

/** The code of the refusal above, so a screen can show it as a notice. */
export const PRACTICE_BLOCKED = "practice_blocked";

export class ApiError extends Error {
  /** `body`: the parsed JSON error body, when there was one (e.g. concerns_undecided's concern_ids, AIC-274). */
  constructor(readonly status: number, readonly code: string | null, readonly detail: string | null = null, readonly body: unknown = null) {
    super(`portal api ${status}${code ? ` ${code}` : ""}`);
    this.name = "ApiError";
  }
}

/** outcome (AIC-252): the latest link's interview outcome (schemas.ts INTERVIEW_OUTCOMES), or "none" for no link yet.
 * q (AIC-268): search on the server, company or owner name. */
export interface AssignmentFilter { portfolio?: 1; view?: "all" | "action" | "waiting" | "completed"; issue?: "missing" | "overdue" | "review"; sort?: "action_due" | "company" | "due"; cycle?: string; status?: string; survey_type?: string; owner_id?: string; outcome?: string; q?: string; contacts?: string }
/** AIC-268: which page of a server-paged list (hooks.ts usePagedLoad): the cursor after the last row shown, a size. */
export interface TodayPage extends Page { q?:string; company?:string; outlook?:"connected"|"disconnected"|"reconnect"|"unknown"; display_status?:string; overdue?:string }
export interface Page { cursor?: string; limit?: number }
/** The Review queue's server-side filters (AIC-268): kind, search (company or reason), one assignment, and item, the
 * lookup of one linked item in that view (the list comes back with it alone, or empty). */
export interface ReviewItemFilter extends Page { kind?: string; q?: string; assignment_id?: string; item?: string }
export interface ResultFilter extends Page { assignment_id?: string }

/** The groups a profile save may carry (packages/core/src/analyst-profile.ts): any of them. */
export interface ProfilePatch {
  languages?: { language: string; level: string }[];
  regions?: string[];
  signature?: { mode: "idc_profile" | "own"; text?: string | null };
  reminders?: { day3: boolean; day7: boolean; slot: boolean };
  extra_cc?: string[];
}
export type ProfileReportReason = ReportReason;

export interface PortalApi {
  previewTargets(): Promise<z.infer<typeof s.previewTargets>>;
  startPreview(analystId: string, reason: string): Promise<z.infer<typeof s.previewAction>>;
  stopPreview(): Promise<z.infer<typeof s.previewAction>>;
  me(): Promise<s.Me>;
  /** The caller's own onboarding progress (AIC-302). Never called in analyst preview (the server refuses it there). */
  onboarding(): Promise<s.OnboardingViewData>;
  /** One onboarding action at the version read; 409 conflict carries the current state in the error body. */
  saveOnboarding(version: number, action: OnboardingAction): Promise<s.OnboardingViewData>;
  /** The caller's own Outlook connection (AIC-158). */
  graphStatus(): Promise<s.GraphStatus>;
  /** A one-time URL (/auth/graph/connect?ticket=…) the browser navigates to; the bearer token never goes in a URL. */
  graphConnect(): Promise<string>;
  /** Asks Graph /me with the caller's stored connection. */
  graphCheck(): Promise<s.GraphCheck>;
  /** Removes the caller's Outlook connection. */
  graphDisconnect(): Promise<void>;
  /** AIC-815: the caller's own profile; or an analyst's (their manager, or an admin). */
  profile(analystId?: string): Promise<s.ProfileView>;
  /** Saves the groups named in `patch` (the analyst's own, or languages and regions of another); answers what changed and the profile. */
  saveProfile(patch: ProfilePatch, analystId?: string): Promise<s.ProfileSaved>;
  /** The caller's Outlook working hours, as booking reads them. */
  profileHours(): Promise<s.ProfileHours>;
  /** "Report a mismatch": an item for the admins in People; the outcome comes back in the bell. */
  reportMismatch(reason: ProfileReportReason, note?: string): Promise<z.infer<typeof s.profileReported>>;
  /** AIC-987: for a caller with no analyst mapping, their open "Request mapping" (none once mapped). */
  mappingRequest(): Promise<z.infer<typeof s.mappingRequest>>;
  /** AIC-987: asks an admin to map the caller's account; one open request per account (`existing` answers a second). */
  requestMapping(): Promise<z.infer<typeof s.mappingRequested>>;
  /** Today: the caller's own queue, or any analyst's (admins) or a team member's (managers, their Entra chain only). */
  queue(analystId?: string, page?: TodayPage): Promise<s.Queue>;
  /** The rail's counts in one call (AIC-181), in the caller's scope. */
  navCounts(): Promise<s.NavCounts>;
  /** AIC-511: the bell's drawer (never called in analyst preview or practice: the bell isn't shown there). */
  notifications(): Promise<s.NotificationList>;
  /** AIC-511: the drawer showed everything up to `through` (its seen_through): the unread count goes to zero. */
  markNotificationsSeen(through: string): Promise<void>;
  /** Filtered on the server, in the caller's scope (AIC-181): owner_id is an analyst id, or "none" for unassigned. */
  assignments(filter: AssignmentFilter, page?: Page): Promise<s.AssignmentList>;
  assignment(id: string): Promise<s.AssignmentDetail>;
  /** AIC-384: the prep watcher's light read (status, prep job, latest spec id and status); the detail's access rule. */
  assignmentPrep(id: string): Promise<s.AssignmentPrep>;
  /** AIC-495: the interview watch's light read (what its signature compares); the detail's access rule. 404 `not found` is the assignment; any other 404 is a server without the route. */
  interviewStatus(id: string): Promise<s.InterviewStatus>;
  /** AIC-479: calls the vendor booked with the analyst (the detail's access rule; not in the analyst preview). */
  analystCalls(id: string): Promise<s.AnalystCalls>;
  /** One click-scoped retained read; consumed once by AssignmentDetail or discarded on scope reset. */
  prefetchAssignment(id: string): Promise<s.AssignmentDetail>;
  spec(id: string): Promise<s.SpecView>;
  /** reason: reject only, optional, at most 500 characters (AIC-181). */
  decideSpec(id: string, action: "approve" | "reject", reason?: string): Promise<void | SpecDecided>;
  /** Queues interview-prep for the assignment (owner or admin; 409 while one is open or once the spec is approved). */
  prepare(assignmentId: string): Promise<s.PrepareResponse>;
  /** Issues an interview link; the link is in this response only. */
  issueLink(assignmentId: string, formOnly: boolean): Promise<s.IssuedLink>;
  /** Build mode only: the current one-time code of a link. */
  otp(sessionId: string): Promise<s.OtpView>;
  /**
   * AIC-482: practice or build mode only: mints a fresh one-time code (the old one stops working) and returns it once.
   * 403 live_mode / forbidden, 409 not_pending, 423 locked, 429 too_soon / mint_limit (retry_after_s).
   */
  mintOtp(sessionId: string): Promise<s.OtpView>;
  /** Admin only (AIC-251): expires one open link, with a reason (audited). 409 not_open / call_live. */
  expireLink(sessionId: string, reason: string): Promise<void>;
  /** Owner or admin (AIC-252): have a partial interview verified, with a reason (recorded and audited). 409 not_partial / call_live. */
  verifyPartial(sessionId: string, reason: string): Promise<z.infer<typeof s.partialVerification>>;
  /** Admin only (AIC-320): the latest post-call-verifier job of an interview and whether it can be re-run. */
  verifierStatus(sessionId: string): Promise<s.VerifierStatus>;
  /** Admin only (AIC-320): re-queue a failed or cancelled verifier (audited). 409 job_open / job_succeeded / … */
  rerunVerifier(sessionId: string): Promise<z.infer<typeof s.verifierRerun>>;
  /** The assignment's invite draft and whether the caller's own Outlook is connected (AIC-108). */
  inviteDraft(assignmentId: string): Promise<s.InviteDraftStatus>;
  /** Creates the invite DRAFT in the caller's own Outlook (never sends); `fresh` replaces the existing draft. */
  createInviteDraft(assignmentId: string, fresh: boolean): Promise<s.InviteDraftResult>;
  /**
   * Owner or admin (AIC-433): records that the invite was sent when detection missed it. Sends nothing. note optional
   * (at most 500); sentOn optional (YYYY-MM-DD; the server keeps it between the draft's creation and now; default now).
   */
  markSent(assignmentId: string, note?: string, sentOn?: string): Promise<s.MarkSentResult>;
  results(status: string, filter?: ResultFilter): Promise<s.ResultList>;
  result(id: string): Promise<s.ResultDetail>;
  /** approve writes to Snowflake (tracker-api appends the rows); reject and needs-follow-up write nothing. */
  decideResult(id: string, action: "approve" | "reject" | "needs-follow-up"): Promise<s.ResultDecision>;
  /** Owner or admin (AIC-259): the analyst's decision on one concern; audited, nothing written to Snowflake. */
  decideConcern(resultId: string, concernId: string, decision: s.ConcernDecisionInput): Promise<s.ConcernDecisionResponse>;
  /** The result's vendor follow-up draft and whether the caller's own Outlook is connected (AIC-261). */
  followUpDraft(resultId: string): Promise<s.FollowUpDraftStatus>;
  /** Creates or rewrites the follow-up DRAFT in the caller's own Outlook (never sends). 409 not_connected. */
  createFollowUpDraft(resultId: string, body: s.FollowUpDraftRequest): Promise<s.FollowUpDraftResult>;
  /** AIC-505: closes (expires) the result's open follow-up interview link, which holds Approve. */
  closeFollowUpInterview(resultId: string): Promise<s.FollowUpInterviewClosed>;
  reviewItems(status: string, filter?: ReviewItemFilter): Promise<s.ReviewItems>;
  /** A session's calls and transcript lines, in the caller's scope (AIC-238). */
  sessionCalls(sessionId: string): Promise<s.SessionCalls>;
  /**
   * One call's recording as a Blob, fetched through /api/portal in ranged chunks with the bearer token (an <audio src>
   * cannot carry it). Stream only: nothing is saved; the player plays the Blob from memory. `onProgress` gets 0..1.
   */
  callAudio(sessionId: string, callId: string, onProgress?: (fraction: number) => void, signal?: AbortSignal): Promise<Blob>;
  /**
   * AIC-246: asks tracker-api (with the bearer token) for the swt_play cookie, which lets an <audio src> on this
   * session's audio URL stream and seek natively. The cookie is HttpOnly and lasts `expires_in_s`.
   */
  callPlayback(sessionId: string): Promise<s.CallPlayback>;
  /** The URL an <audio src> streams a call's compressed copy from, once callPlayback has set the cookie. */
  callStreamUrl(sessionId: string, callId: string): string;
  resolveReviewItem(id: string, status: "resolved" | "dismissed", note?: string): Promise<void>;
  /** Back to open (AIC-182): the Undo on a resolve or dismiss. Owner or admin only, like resolving. */
  reopenReviewItem(id: string): Promise<void>;
  dashboard(cycle?: string): Promise<s.Dashboard>;
  /** Collection pacing for the caller's scope (AIC-125): actual vs target over time, and who is behind. */
  pacing(cycle?: string): Promise<s.Pacing>;
  /** Insights → Data outcomes (AIC-145): IDC's estimates against vendors' answers, in the caller's scope. */
  outcomes(filter: s.OutcomeFilter): Promise<s.Outcomes>;
  /** Insights → Cost (AIC-148): cost per interview in the caller's scope, with the shared filters. */
  insightsCost(filter: s.CostFilter): Promise<s.InsightsCost>;
  /** Insights → Modeler digest (AIC-513): what approved vendor answers changed against IDC's estimates, in the caller's scope. */
  modelerDigest(filter: s.DigestFilter): Promise<s.ModelerDigest>;
  /** An Insights tab's CSV, in the caller's scope; the server audits every export (`insights:export`). */
  exportCsv(tab: "outcomes" | "cost" | "digest", filter: Record<string, string | undefined>): Promise<{ filename: string; text: string }>;
  /** Insights → Workflow cycle times (AIC-146): stage durations, work in progress and stuck queues, in the caller's scope. */
  workflow(filter: s.WorkflowFilter): Promise<s.Workflow>;
  /** Insights → Vendor funnel (AIC-143): link → code → consent → interview → review → rating, in the caller's scope. */
  funnel(filter: s.FunnelFilter): Promise<s.Funnel>;
  /** Insights → Call quality (AIC-144): voice call quality distributions and outlier calls, in the caller's scope. */
  callQuality(filter: s.CallQualityFilter): Promise<s.CallQuality>;
  /** Insights → Verifier accuracy (AIC-514): how often analysts overrule the post-call-verifier, in the caller's scope. */
  verifierAccuracy(filter: s.VerifierAccuracyFilter): Promise<s.VerifierAccuracy>;
  /** Admins: the overridden cases as a CSV of identifiers only (the verifier regression set); audited. */
  exportVerifierOverrides(filter: s.VerifierAccuracyFilter): Promise<{ filename: string; text: string }>;
  /** Admins: every cycle's dates and pacing settings. */
  adminCycles(): Promise<s.CycleTimeline[]>;
  /** Admins: set a cycle's dates and pacing settings (audited). */
  saveCycle(cycleId: string, settings: s.CycleSettings): Promise<s.CycleTimeline>;
  /** Admins: every program default (AIC-150). */
  adminSettings(): Promise<s.SettingView[]>;
  /** Admins: who changed a setting, when and why, newest first. */
  settingHistory(key: string): Promise<s.SettingHistoryEntry[]>;
  /** Admins: change a program default, with a reason (audited `settings:update`). */
  saveSetting(key: string, value: unknown, reason: string): Promise<s.SettingSaved>;
  /** Admins: the platform values, read-only (set only by a deploy). */
  platform(): Promise<s.PlatformPanel>;
  /** Admins (AIC-93): the retention dry-run report, read-only: what would expire under the set periods. */
  retentionReport(): Promise<s.RetentionReport>;
  /** Admins (AIC-149): a new draft cycle with empty dates, copying the program defaults and the latest targets. */
  createCycle(cycleId: string, isDev: boolean): Promise<s.CycleTimeline>;
  /** Admins: the cycle's readiness checklist, computed from real state. */
  cycleReadiness(cycleId: string): Promise<s.Readiness>;
  /** Admins: what a date change on an open cycle would do, before it's saved. */
  cycleImpact(cycleId: string, dates: s.CycleDates): Promise<s.CycleImpact>;
  /** Admins (AIC-91): the cycle's SharePoint imports (the current file of each kind) and whether the caller's Outlook connection can read SharePoint. */
  cycleImports(cycleId: string): Promise<s.CycleImports>;
  /** Admins: import the pasted SharePoint files for the cycle, as the caller (read-only Graph; audited). */
  importFromSharePoint(cycleId: string, links: s.ImportLinks): Promise<s.ImportResult[]>;
  /** Admins: import a file from their computer into a slot (multipart; same checks and provenance as a link; audited). */
  uploadImport(cycleId: string, kind: s.ImportKind, file: File): Promise<s.ImportResult>;
  /** Admins: clear a slot's current file so the built-in is used again (templates and the thank-you PDF; audited). */
  clearImport(cycleId: string, kind: s.ImportKind): Promise<s.ClearResponse>;
  /** Admins and managers (AIC-95): the cycles the Survey_list reconciliation can show. */
  reconciliationCycles(): Promise<s.ReconciliationCycles>;
  /** Admins and managers: the cycle's Survey_list reconciliation report (read-only, informational). */
  reconciliation(cycleId: string, page?: Page & {kind?:string; q?:string}): Promise<s.Reconciliation>;
  /** Admins and managers: every difference as CSV; the server audits the export (`reconciliation:export`). */
  reconciliationExport(cycleId:string, pins?:{snapshot_id:string;assignments_revision:string}):Promise<s.Reconciliation>;
  reconciliationCsv(cycleId: string, pins?:{snapshot_id:string;assignments_revision:string}): Promise<{ filename: string; text: string }>;
  /** The assignment's vendor contacts (owner, admin, or a manager over the chain read-only). */
  assignmentContacts(assignmentId: string): Promise<s.AssignmentContacts>;
  /** Owner or admin: add a contact (audited). */
  addContact(assignmentId: string, contact: s.NewContact): Promise<s.VendorContact>;
  /** Owner or admin: edit a contact, or mark it bounced / retired (audited). */
  editContact(assignmentId: string, contactId: string, edit: s.ContactEdit): Promise<s.VendorContact>;
  /** Owner or admin (AIC-510): mark a contact's quality warning checked, with a note (audited). */
  checkContactWarning(assignmentId: string, contactId: string, check: { kind: s.ContactWarning["kind"]; note: string }): Promise<s.ContactWarning>;
  /** Admins (AIC-510): every flagged contact in the cycle. */
  contactWarnings(cycleId: string): Promise<s.CycleContactWarnings>;
  contactWarningsCsv(cycleId: string): Promise<{ filename: string; text: string }>;
  /** AIC-506: colleagues a vendor named on the interview page (owner, admin, or a manager over the chain read-only). */
  colleagueNominations(assignmentId: string): Promise<s.ColleagueNominations>;
  /** Owner or admin: confirm (the colleague becomes an active contact) or dismiss a proposed colleague (audited). */
  decideNomination(assignmentId: string, nominationId: string, decision: "confirm" | "dismiss", acks?: s.NominationAcks): Promise<s.NominationDecision>;
  /** Owner or admin: the confirmed colleague's invite draft, in the caller's own Outlook (or the practice outbox). */
  createColleagueInviteDraft(assignmentId: string, nominationId: string, acknowledgeProgress?: boolean): Promise<s.ColleagueInviteDraftResult>;
  /** Admins: move the cycle through its lifecycle (audited). */
  transitionCycle(cycleId: string, to: s.CycleState, extensionUntil?: string): Promise<s.CycleTimeline>;
  /** Admins: "unconfirmed" is Needs action (owners marked as left are not in it); "left" lists those (AIC-133). */
  /** Admins (AIC-556): Admin → People, everyone's mapping, last sign-in and workload; and one person's current-cycle assignments for its dialogs. */
  people(): Promise<s.People>;
  personAssignments(analystId: string): Promise<s.PersonAssignments>;
  confirmMapping(analystId: string, upn: string): Promise<void>;
  /** Admins: send a confirmed owner mapping back to the queue. */
  unconfirmMapping(analystId: string): Promise<void>;
  /** Admins: mark an unmapped owner as left (AIC-133); their assignments stay unassigned. unmarkLeft undoes it. */
  markLeft(analystId: string): Promise<void>;
  unmarkLeft(analystId: string): Promise<void>;
  /** Admins (AIC-215): a cycle's anomalies, by kind (null = every kind) and status. */
  anomalies(cycleId: string, kind: string | null, status: s.AnomalyStatus | "all", page?: Page): Promise<s.Anomalies>;
  /** Admins: dismiss (the sync won't raise it again) or resolve an open anomaly, with a reason; audited. */
  closeAnomaly(anomalyId: string, status: "dismissed" | "resolved", reason: string): Promise<void>;
  /** Admins: add an IDC staff member who is not a Snowflake owner, as a confirmed analyst. */
  addStaff(upn: string, displayName: string): Promise<s.StaffResponse>;
  /** Confirmed analysts (reassignment targets): every one for admins, the Entra reporting chain and self for managers. */
  analysts(): Promise<s.ConfirmedAnalyst[]>;
  /** Admins and managers: work an assignment as another analyst, with a reason; clearReassign returns it to the Snowflake owner. */
  reassign(assignmentId: string, analystId: string, reason: string): Promise<void>;
  clearReassign(assignmentId: string): Promise<void>;
  /** AIC-512, admins and managers: reassign several assignments at once (each is moved, audited and refused on its own). */
  bulkReassign(assignmentIds: string[], analystId: string, reason: string): Promise<s.BulkReassignResponse>;
  /** Practice mode (AIC-463). Start, list, reset and restore-spec run in real scope: they never carry the practice header. */
  startPractice(assignmentId: string, forAnalystId?: string): Promise<s.PracticeStarted>;
  practiceSessions(all?: boolean): Promise<s.PracticeList>;
  resetPractice(practiceId: string): Promise<void>;
  restorePracticeSpec(practiceId: string): Promise<s.PracticeRestored>;
  /** The active practice session's outbox, and its simulated send (no date: today), mark-as-sent (a date) and discard. */
  practiceOutbox(): Promise<s.PracticeOutbox>;
  /** The practice copy's assignment id: the one assignment the session can see (the id is given, so no session need be active yet). */
  practiceCopyId(practiceId: string): Promise<string | null>;
  practiceSend(draftId: string, sentOn?: string): Promise<void>;
  practiceDiscard(draftId: string): Promise<void>;
}

/** The practice hook's refusals that end the session (hook.ts): 410 practice_reset, 404 practice_not_found, 403 practice_forbidden / practice_preview. */
const practiceEnded = (status: number, code: string | null): boolean =>
  status === 410 || (status === 404 && code === "practice_not_found") || (status === 403 && (code === "practice_forbidden" || code === "practice_preview"));

const parseJson = (text: string): unknown => {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
};

/** What deciding a spec answers beyond the decision: in practice, approving's live practice link, once (AIC-463). */
export interface SpecDecided { practiceLink: string | null }

export interface ClientOptions {
  fetchImpl?: typeof fetch;
}

/** Controls are attached to this client's facades, never to cached user data globally. Test doubles need none. */
interface ReadControls {
  bind(signal: AbortSignal): PortalApi;
  clear(): void;
  scope(value: string): void;
  subscribe(listener: () => void): () => void;
}
export const isAbortError = (error: unknown): boolean => error !== null && typeof error === "object" && "name" in error && error.name === "AbortError";
const readControls = new WeakMap<PortalApi, ReadControls>();
/** Bind GET lifetimes only: mutation methods still run exactly once and are never cancelled/retried here. */
export const withReadSignal = (api: PortalApi, signal: AbortSignal): PortalApi => readControls.get(api)?.bind(signal) ?? api;
export const clearPortalReads = (api: PortalApi): void => { readControls.get(api)?.clear(); };
export const setPortalReadScope = (api: PortalApi, scope: string): void => { readControls.get(api)?.scope(scope); };
export const onPortalMutation = (api: PortalApi, listener: () => void): (() => void) => readControls.get(api)?.subscribe(listener) ?? (() => {});

/** Bytes per ranged request for call audio: 4 MiB, about 65 s of 16 kHz 16-bit stereo, so progress moves. */
export const CALL_AUDIO_CHUNK = 4 << 20;

/** AIC-502: the pause before a read's single retry after a bare gateway 503. */
export const GATEWAY_RETRY_MS = 600;

export function createPortalApi(auth: Auth, opts: ClientOptions = {}): PortalApi {
  const doFetch = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));

  const reads = new ReadPool<{ res: Response; text: string }>();
  const listeners = new Set<() => void>();
  let lifetime = 0;
  let scope = "";
  const resetReads = () => { lifetime++; reads.clear(true); };
  const controls: ReadControls = {
    bind: makeApi,
    clear: resetReads,
    scope: (value) => { if (value !== scope) { scope = value; resetReads(); } },
    subscribe: (listener) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
  };

  function makeApi(signal?: AbortSignal): PortalApi {
    /** One authenticated request; throws ApiError (with the server's error / message) on a non-2xx response. */
    async function send(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, accept: string, body?: unknown, retainFor = 0, practiceOverride?: string): Promise<{ res: Response; text: string }> {
      const auditedOtp = method === "GET" && /^\/sessions\/[^/]+\/otp$/.test(path);
      if (method === "GET" && !auditedOtp) signal?.throwIfAborted();
      const started = lifetime;
      // The practice session this request belongs to (AIC-463): the header only on an allowlisted route, and the read
      // cache is keyed by it, so practice data never answers a real read or the other way round.
      // In practice a write outside the practice allowlist would change REAL data: refused before any request.
      if (method !== "GET" && activePracticeId() && !practiceMutationAllowed(path)) {
        throw new ApiError(409, PRACTICE_BLOCKED, "That action isn't available in practice because it would change real data. Exit practice to do it.");
      }
      const practiceId = practiceHeaderApplies(path) ? practiceOverride ?? activePracticeId() : null;
      const token = await auth.getToken();
      if (method === "GET" && !auditedOtp) signal?.throwIfAborted();
      if (method === "GET" && !auditedOtp && started !== lifetime) throw new DOMException("Read scope changed", "AbortError");
      reads.identity(token);
      if (!token) throw new ApiError(401, "no_session");
      const request = async (readSignal?: AbortSignal) => {
        const headers: Record<string, string> = { authorization: `Bearer ${token}`, accept };
        const form = typeof FormData !== "undefined" && body instanceof FormData;
        if (body !== undefined && !form) headers["content-type"] = "application/json";
        if (practiceId) headers[PRACTICE_HEADER] = practiceId;
        const res = await doFetch(`/api/portal${path}`, { method, headers, body: body === undefined ? undefined : form ? body : JSON.stringify(body), cache: "no-store", signal: readSignal });
        const text = await res.text();
        if (!res.ok) {
          const err = (parseJson(text) ?? {}) as { error?: unknown; message?: unknown };
          if (err.error === "preview_invalid") { resetReads(); window.dispatchEvent(new Event("swt-preview-invalid")); }
          if (res.status === 401 || res.status === 403) reads.clear();
          // The practice hook says the session is over (reset, not found, or not the caller's): leave practice.
          if (practiceId && practiceEnded(res.status, typeof err.error === "string" ? err.error : null) && activePracticeId() === practiceId) {
            reads.clear();
            endPractice({ notice: res.status === 410 ? "That practice session was reset, so you're back on the real assignment." : "That practice session is no longer available to you, so you're back on the real assignment." });
          }
          throw new ApiError(res.status, typeof err.error === "string" ? err.error : null, typeof err.message === "string" ? err.message : null, parseJson(text) ?? null);
        }
        return { res, text };
      };
      if (method === "GET") {
        // OTP is an audited per-action reveal, not a background read: never coalesce or cancel it.
        if (auditedOtp) return request();
        // Exports are audited per action; don't deduplicate them. Audio retains its dedicated transport below.
        if (accept !== "application/json" || /^\/admin\/cycles\/[^/]+\/reconciliation\/export(?:\?|$)/.test(path)) return request(signal);
        const ttl = path === "/me/counts" || path === "/analysts" ? 10_000 : 0;
        // AIC-502: a gateway blip (503 with no tracker-api error code, from the load balancer while a task restarts) gets
        // one quiet retry of this idempotent read; an app-level 503 (export_busy, *_unavailable) carries a code and is not retried.
        const once = async (readSignal?: AbortSignal) => {
          try { return await request(readSignal); }
          catch (e) {
            if (!(e instanceof ApiError) || e.status !== 503 || e.code !== null) throw e;
            await new Promise<void>((resolve) => setTimeout(resolve, GATEWAY_RETRY_MS));
            readSignal?.throwIfAborted();
            return request(readSignal);
          }
        };
        return reads.read(`${practiceId ?? "-"} ${accept} ${path}`, once, signal, ttl, retainFor);
      }
      // Detach before AND after: an overlapping read must not repopulate data from before the write settled.
      // AIC-584: a preview start/stop is the exception. Reads are aborted only once the switch succeeded (the document
      // is replaced then): a failed one changed nothing, so the screen's in-flight reads must still land (the hooks
      // never retry a read that was aborted under them, which left a skeleton forever).
      const scopeChange = path.startsWith("/analyst-preview/");
      // AIC-511: marking notifications seen changes only the caller's own marker: no cached read is stale and no badge but
      // the bell's moves (the Shell clears that one itself), so opening the bell never refetches the page behind it.
      const quiet = path === "/me/notifications/seen";
      // Only the counts read (the bell's number) is dropped, so the next one asks the server again.
      const forgetCounts = () => reads.forget((key) => key.endsWith(" /me/counts"));
      if (!scopeChange) { if (quiet) forgetCounts(); else reads.clear(); }
      let switched = false;
      try { const done = await request(); switched = true; return done; }
      finally {
        if (scopeChange) { if (switched) resetReads(); } else if (quiet) forgetCounts(); else reads.clear();
        // Onboarding, playback and notifications seen do not change queue badges or analyst metadata.
        if (path !== "/me/onboarding" && !path.endsWith("/playback") && !quiet) for (const listener of listeners) listener();
      }
    }

    async function call<T extends z.ZodType>(schema: T, method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown, retainFor = 0, practiceOverride?: string): Promise<z.infer<T>> {
      const { text } = await send(method, path, "application/json", body, retainFor, practiceOverride);
      try { return schema.parse(parseJson(text)); }
      catch (error) { reads.clear(); throw error; }
    }

    const q = (params: Record<string, string | null | undefined>) => {
      const u = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v) u.set(k, v);
      const str = u.toString();
      return str ? `?${str}` : "";
    };
    const id = (v: string) => encodeURIComponent(v);
    const pageParams = (p?: Page) => ({ cursor: p?.cursor, limit: p?.limit ? String(p.limit) : undefined });

    const api: PortalApi = {
      previewTargets: () => call(s.previewTargets, "GET", "/analyst-preview/targets"),
      // A preview ends practice in place first: the preview reads real data only.
      startPreview: (analyst_id, reason) => { endPractice({ stay: true }); return call(s.previewAction, "POST", "/analyst-preview/start", { analyst_id, reason }); },
      // AIC-254: 204 when there was no preview cookie to clear (nothing audited).
      stopPreview: async () => {
        const { res, text } = await send("POST", "/analyst-preview/stop", "application/json");
        return res.status === 204 ? { ok: true as const } : s.previewAction.parse(parseJson(text));
      },
      me: () => call(s.me, "GET", "/me"),
      onboarding: () => call(s.onboardingView, "GET", "/me/onboarding"),
      saveOnboarding: (version, action) => call(s.onboardingView, "PUT", "/me/onboarding", { version, action }),
      graphStatus: () => call(s.graphStatus, "GET", "/me/graph"),
      graphConnect: async () => (await call(s.graphConnect, "POST", "/me/graph/connect")).url,
      graphCheck: () => call(s.graphCheck, "POST", "/me/graph/check"),
      graphDisconnect: async () => {
        await call(s.graphDisconnect, "POST", "/me/graph/disconnect");
      },
      profile: (analystId) => call(s.profileView, "GET", analystId ? `/analysts/${id(analystId)}/profile` : "/me/profile"),
      saveProfile: (patch, analystId) => call(s.profileSaved, "PATCH", analystId ? `/analysts/${id(analystId)}/profile` : "/me/profile", patch),
      profileHours: () => call(s.profileHours, "GET", "/me/profile/hours"),
      reportMismatch: (reason, note) => call(s.profileReported, "POST", "/me/profile/report", { reason, ...(note ? { note } : {}) }),
      mappingRequest: () => call(s.mappingRequest, "GET", "/me/mapping-request"),
      requestMapping: () => call(s.mappingRequested, "POST", "/me/mapping-request", {}),
      queue: (analystId, page) => call(page ? s.pagedQueue : s.queue, "GET", `/me/queue${q({ analyst_id: analystId, ...page, limit:page?.limit ? String(page.limit) : undefined })}`),
      navCounts: () => call(s.navCounts, "GET", "/me/counts"),
      notifications: () => call(s.notificationList, "GET", "/me/notifications"),
      markNotificationsSeen: async (through) => { await call(s.notificationsSeen, "POST", "/me/notifications/seen", { through }); },
      assignments: (f, p) => call(s.assignmentList, "GET", `/assignments${q({ cycle: f.cycle, status: f.status, survey_type: f.survey_type, owner_id: f.owner_id, outcome: f.outcome, contacts: f.contacts, q: f.q, portfolio: f.portfolio ? "1" : undefined, view: f.view, issue: f.issue, sort: f.sort, ...pageParams(p) })}`),
      assignment: (a) => call(s.assignmentDetail, "GET", `/assignments/${id(a)}`),
      assignmentPrep: (a) => call(s.assignmentPrep, "GET", `/assignments/${id(a)}/prep`),
      interviewStatus: (a) => call(s.interviewStatus, "GET", `/assignments/${id(a)}/interview-status`),
      analystCalls: (a) => call(s.analystCalls, "GET", `/assignments/${id(a)}/analyst-calls`),
      // One user navigation may hand this completed response to its screen. It is
      // consumed once, expires quickly, and is invalidated by the navigation signal.
      prefetchAssignment: (a) => call(s.assignmentDetail, "GET", `/assignments/${id(a)}`, undefined, 5_000),
      spec: (sp) => call(s.specView, "GET", `/specs/${id(sp)}`),
      decideSpec: async (sp, action, reason) => {
        const r = action === "reject" ? reason?.trim() : "";
        const out = await call(s.decision, "POST", `/specs/${id(sp)}/${action}`, r ? { reason: r } : undefined);
        return { practiceLink: out.invite?.practice_link ?? null };
      },
      prepare: (a) => call(s.prepareResponse, "POST", `/assignments/${id(a)}/prepare`),
      issueLink: (a, formOnly) => call(s.issuedLink, "POST", `/assignments/${id(a)}/sessions`, { form_only: formOnly }),
      otp: (sid) => call(s.otpView, "GET", `/sessions/${id(sid)}/otp`),
      mintOtp: (sid) => call(s.otpView, "POST", `/sessions/${id(sid)}/otp`),
      expireLink: async (sid, reason) => {
        await call(s.expiredLink, "POST", `/sessions/${id(sid)}/expire`, { reason: reason.trim() });
      },
      verifyPartial: (sid, reason) => call(s.partialVerification, "POST", `/sessions/${id(sid)}/verify-partial`, { reason: reason.trim() }),
      verifierStatus: (sid) => call(s.verifierStatus, "GET", `/sessions/${id(sid)}/verifier`),
      rerunVerifier: (sid) => call(s.verifierRerun, "POST", `/sessions/${id(sid)}/verifier/rerun`, {}),
      inviteDraft: (a) => call(s.inviteDraftStatus, "GET", `/assignments/${id(a)}/invite-draft`),
      createInviteDraft: (a, fresh) => call(s.inviteDraftResult, "POST", `/assignments/${id(a)}/invite-draft`, fresh ? { new: true } : {}),
      markSent: (a, note, sentOn) => call(s.markSentResult, "POST", `/assignments/${id(a)}/mark-sent`, {
        ...(note?.trim() ? { note: note.trim() } : {}), ...(sentOn ? { sent_on: sentOn } : {}),
      }),
      results: (status, f) => call(s.resultList, "GET", `/results${q({ status, assignment_id: f?.assignment_id, ...pageParams(f) })}`),
      result: (r) => call(s.resultDetail, "GET", `/results/${id(r)}`),
      decideResult: (r, action) => call(s.resultDecision, "POST", `/results/${id(r)}/${action}`),
      decideConcern: (r, cid, d) => call(s.concernDecisionResponse, "POST", `/results/${id(r)}/concerns/${id(cid)}/decision`, {
        disposition: d.disposition,
        ...(d.note?.trim() ? { note: d.note.trim() } : {}),
        ...(d.recommendation_used === undefined ? {} : { recommendation_used: d.recommendation_used }),
        ...(d.blocking === undefined ? {} : { blocking: d.blocking }),
      }),
      followUpDraft: (r) => call(s.followUpDraftStatus, "GET", `/results/${id(r)}/follow-up-draft`),
      createFollowUpDraft: (r, body) => call(s.followUpDraftResult, "POST", `/results/${id(r)}/follow-up-draft`, s.followUpDraftRequest.parse(body)),
      closeFollowUpInterview: (r) => call(s.followUpInterviewClosed, "POST", `/results/${id(r)}/follow-up-interview/close`),
      reviewItems: (status, f) => call(s.reviewItems, "GET", `/review-items${q({ status, kind: f?.kind, q: f?.q, assignment_id: f?.assignment_id, item: f?.item, ...pageParams(f) })}`),
      sessionCalls: (sid) => call(s.sessionCalls, "GET", `/sessions/${id(sid)}/calls`),
      callAudio: async (sid, callId, onProgress, signal) => {
        const path = `/api/portal/sessions/${id(sid)}/calls/${id(callId)}/audio`;
        const parts: ArrayBuffer[] = [];
        let start = 0;
        let total = Infinity;
        while (start < total) {
          const token = await auth.getToken();
          if (!token) throw new ApiError(401, "no_session");
          const res = await doFetch(path, {
            method: "GET", headers: { authorization: `Bearer ${token}`, accept: "audio/wav", range: `bytes=${start}-${start + CALL_AUDIO_CHUNK - 1}` },
            cache: "no-store", signal,
          });
          if (res.status === 200) { // the server sent it whole
            parts.splice(0, parts.length, await res.arrayBuffer());
            break;
          }
          if (res.status !== 206) {
            const err = (parseJson(await res.text()) ?? {}) as { error?: unknown };
            throw new ApiError(res.status, typeof err.error === "string" ? err.error : null);
          }
          const size = Number(/\/(\d+)$/.exec(res.headers.get("content-range") ?? "")?.[1]);
          const buf = await res.arrayBuffer();
          if (!Number.isFinite(size) || buf.byteLength === 0) throw new ApiError(502, "bad_range");
          total = size;
          parts.push(buf);
          start += buf.byteLength;
          onProgress?.(Math.min(1, start / total));
        }
        onProgress?.(1);
        return new Blob(parts, { type: "audio/wav" });
      },
      callPlayback: (sid) => call(s.callPlayback, "POST", `/sessions/${id(sid)}/playback`),
      callStreamUrl: (sid, callId) => `/api/portal/sessions/${id(sid)}/calls/${id(callId)}/audio?format=m4a`,
      resolveReviewItem: async (item, status, note) => {
        await call(s.resolved, "POST", `/review-items/${id(item)}/resolve`, note ? { status, note } : { status });
      },
      reopenReviewItem: async (item) => {
        await call(s.reopened, "POST", `/review-items/${id(item)}/reopen`);
      },
      dashboard: (cycle) => call(s.dashboard, "GET", `/dashboard${q({ cycle })}`),
      pacing: (cycle) => call(s.pacing, "GET", `/pacing${q({ cycle })}`),
      outcomes: (f) => call(s.outcomes, "GET", `/insights/outcomes${q({ cycle: f.cycle, team: f.team, analyst: f.analyst, survey_type: f.survey_type, build: f.build })}`),
      insightsCost: (f) => call(s.insightsCost, "GET", `/insights/cost${q({ cycle: f.cycle, team: f.team, analyst: f.analyst, survey_type: f.survey_type, from: f.from, to: f.to })}`),
      modelerDigest: (f) => call(s.modelerDigest, "GET", `/insights/digest${q({ cycle: f.cycle, team: f.team, analyst: f.analyst, build: f.build, modeler: f.modeler, market: f.market, min_delta: f.min_delta, group: f.group, period: f.period })}`),
      exportCsv: async (tab, f) => {
        const { res, text } = await send("GET", `/insights/${tab}.csv${q(f)}`, "text/csv");
        const named = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1];
        return { filename: named ?? `insights-${tab}.csv`, text };
      },
      workflow: (f) => call(s.workflow, "GET", `/insights/workflow${q({ cycle: f.cycle, team: f.team, analyst: f.analyst, survey_type: f.survey_type })}`),
      funnel: (f) => call(s.funnel, "GET", `/insights/funnel${q({ cycle: f.cycle, team: f.team, analyst: f.analyst, survey_type: f.survey_type, build: f.build })}`),
      callQuality: (f) => call(s.callQuality, "GET", `/insights/calls${q({ cycle: f.cycle, team: f.team, analyst: f.analyst })}`),
      verifierAccuracy: (f) => call(s.verifierAccuracy, "GET", `/insights/verifier-accuracy${q({ cycle: f.cycle, team: f.team, analyst: f.analyst, survey_type: f.survey_type })}`),
      exportVerifierOverrides: async (f) => {
        const { res, text } = await send("GET", `/insights/verifier-overrides.csv${q({ cycle: f.cycle, team: f.team, analyst: f.analyst, survey_type: f.survey_type })}`, "text/csv");
        const named = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1];
        return { filename: named ?? "verifier-overrides.csv", text };
      },
      adminCycles: () => call(s.adminCycles, "GET", "/admin/cycles"),
      saveCycle: (cycleId, settings) => call(s.cycleTimeline, "PUT", `/admin/cycles/${id(cycleId)}`, settings),
      adminSettings: async () => (await call(s.settingsList, "GET", "/admin/settings")).settings,
      settingHistory: async (key) => (await call(s.settingHistory, "GET", `/admin/settings/${id(key)}/history`)).history,
      saveSetting: (key, value, reason) => call(s.settingSaved, "PUT", `/admin/settings/${id(key)}`, { value, reason }),
      platform: () => call(s.platformPanel, "GET", "/admin/platform"),
      retentionReport: () => call(s.retentionReport, "GET", "/admin/retention"),
      createCycle: (cycleId, isDev) => call(s.cycleTimeline, "POST", "/admin/cycles", { id: cycleId, is_dev: isDev }),
      cycleReadiness: (cycleId) => call(s.readiness, "GET", `/admin/cycles/${id(cycleId)}/readiness`),
      cycleImpact: (cycleId, dates) => call(s.cycleImpact, "POST", `/admin/cycles/${id(cycleId)}/impact`, dates),
      cycleImports: (cycleId) => call(s.cycleImports, "GET", `/admin/cycles/${id(cycleId)}/imports`),
      importFromSharePoint: async (cycleId, links) => (await call(s.importResponse, "POST", `/admin/cycles/${id(cycleId)}/imports`, links)).results,
      uploadImport: async (cycleId, kind, file) => {
        const form = new FormData();
        form.append("file", file, file.name);
        const r = (await call(s.importResponse, "POST", `/admin/cycles/${id(cycleId)}/imports/${id(kind)}/upload`, form)).results[0];
        if (!r) throw new ApiError(500, "no_result");
        return r;
      },
      clearImport: (cycleId, kind) => call(s.clearResponse, "POST", `/admin/cycles/${id(cycleId)}/imports/${id(kind)}/clear`),
      reconciliationCycles: () => call(s.reconciliationCycles, "GET", "/admin/reconciliation"),
      reconciliation: (cycleId, page) => call(s.reconciliation, "GET", `/admin/cycles/${id(cycleId)}/reconciliation${q({...page,limit:page?.limit?String(page.limit):undefined})}`),
      reconciliationExport:(cycleId,pins)=>call(s.reconciliation,"GET",`/admin/cycles/${id(cycleId)}/reconciliation/export${q({format:"json",...pins})}`),
      reconciliationCsv: async (cycleId,pins) => {
        const { res, text } = await send("GET", `/admin/cycles/${id(cycleId)}/reconciliation/export${q({format:"csv",...pins})}`, "text/csv");
        const named = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1];
        return { filename: named ?? `survey-list-reconciliation-${cycleId}.csv`, text };
      },
      assignmentContacts: (a) => call(s.assignmentContacts, "GET", `/assignments/${id(a)}/contacts`),
      addContact: (a, contact) => call(s.vendorContact, "POST", `/assignments/${id(a)}/contacts`, contact),
      editContact: (a, contactId, edit) => call(s.vendorContact, "PATCH", `/assignments/${id(a)}/contacts/${id(contactId)}`, edit),
      checkContactWarning: (a, contactId, check) => call(s.contactWarning, "POST", `/assignments/${id(a)}/contacts/${id(contactId)}/warnings/check`, check),
      contactWarnings: (cycleId) => call(s.cycleContactWarnings, "GET", `/admin/cycles/${id(cycleId)}/contact-warnings`),
      contactWarningsCsv: async (cycleId) => {
        const { res, text } = await send("GET", `/admin/cycles/${id(cycleId)}/contact-warnings${q({ format: "csv" })}`, "text/csv");
        const named = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1];
        return { filename: named ?? `contact-warnings-${cycleId}.csv`, text };
      },
      colleagueNominations: (a) => call(s.colleagueNominations, "GET", `/assignments/${id(a)}/nominations`),
      decideNomination: (a, n, decision, acks) => call(s.nominationDecision, "POST", `/assignments/${id(a)}/nominations/${id(n)}/${decision}`, acks ?? {}),
      createColleagueInviteDraft: (a, n, ack) => call(s.colleagueInviteDraftResult, "POST", `/assignments/${id(a)}/nominations/${id(n)}/invite-draft`, ack ? { acknowledge_progress: true } : {}),
      transitionCycle: (cycleId, to, extensionUntil) =>
        call(s.cycleTimeline, "POST", `/admin/cycles/${id(cycleId)}/transition`, extensionUntil ? { to, extension_until: extensionUntil } : { to }),
      people: () => call(s.people, "GET", "/admin/people"),
      personAssignments: (analystId) => call(s.personAssignments, "GET", `/admin/people/${id(analystId)}/assignments`),
      confirmMapping: async (analystId, upn) => {
        await call(s.confirmResponse, "POST", `/admin/analysts/${id(analystId)}/confirm`, { upn });
      },
      unconfirmMapping: async (analystId) => {
        await call(s.unconfirmResponse, "DELETE", `/admin/analysts/${id(analystId)}/confirm`);
      },
      markLeft: async (analystId) => {
        await call(s.leftResponse, "POST", `/admin/analysts/${id(analystId)}/left`);
      },
      unmarkLeft: async (analystId) => {
        await call(s.unleftResponse, "DELETE", `/admin/analysts/${id(analystId)}/left`);
      },
      anomalies: (cycleId, kind, status, page) => call(s.anomalies, "GET", `/admin/anomalies${q({ cycle: cycleId, kind, status, ...pageParams(page) })}`),
      closeAnomaly: async (anomalyId, status, reason) => {
        await call(s.closeAnomalyResponse, "POST", `/admin/anomalies/${id(anomalyId)}/close`, { status, reason });
      },
      addStaff: (upn, displayName) => call(s.staffResponse, "POST", "/analysts/staff", { upn, display_name: displayName }),
      analysts: () => call(s.confirmedAnalysts, "GET", "/analysts"),
      startPractice: (assignmentId, forAnalystId) => call(s.practiceStarted, "POST", "/practice", { assignment_id: assignmentId, ...(forAnalystId ? { for_analyst_id: forAnalystId } : {}) }),
      practiceSessions: (all) => call(s.practiceList, "GET", `/practice${q({ all: all ? "1" : undefined })}`),
      resetPractice: async (pid) => { await call(s.practiceReset, "POST", `/practice/${id(pid)}/reset`); },
      restorePracticeSpec: (pid) => call(s.practiceRestored, "POST", `/practice/${id(pid)}/restore-spec`),
      practiceCopyId: async (pid) => (await call(s.assignmentList, "GET", `/assignments${q({ limit: "1" })}`, undefined, 0, pid)).assignments[0]?.id ?? null,
      practiceOutbox: () => call(s.practiceOutbox, "GET", "/practice/outbox"),
      practiceSend: async (draftId, sentOn) => { await call(s.practiceDraftAction, "POST", `/practice/outbox/${id(draftId)}/mark-sent`, sentOn ? { sent_on: sentOn } : {}); },
      practiceDiscard: async (draftId) => { await call(s.practiceDraftAction, "POST", `/practice/outbox/${id(draftId)}/discard`); },
      reassign: async (a, analystId, reason) => {
        await call(s.reassignResponse, "POST", `/assignments/${id(a)}/reassign`, { analyst_id: analystId, reason });
      },
      bulkReassign: (ids, analystId, reason) => call(s.bulkReassignResponse, "POST", "/assignments/reassign-bulk", { assignment_ids: ids, analyst_id: analystId, reason }),
      clearReassign: async (a) => {
        await call(s.reassignResponse, "DELETE", `/assignments/${id(a)}/reassign`);
      },
    };
    readControls.set(api, controls);
    return api;
  }
  return makeApi();
}
